import { useClientHints } from '@sbt-web/hooks';
import { ActivePromotionRibbon, DeviceWidth } from '@sbt-web/ui';
import { shouldReduceDataUse } from '@sbt-web/utils';
import classnames from 'classnames';
import React from 'react';
import classes from './CardImage.module.scss.js';

const CameraIcon = ({ count, assetsBase }) => (React.createElement("img", { 
    // The alt is plural because the icon isn't shown if there's only one image.
    alt: `Questo annuncio ha ${count} immagini`, src: `${assetsBase}/static/icons/cactus/camera.svg`, width: 16, height: 16, loading: "lazy", decoding: "async" }));
const sizeToImageRule = new Map([
    ['smallDesktop', 'card-desktop-new-small'],
    ['largeDesktop', 'card-desktop-new-large'],
    ['smallMobile', 'card-mobile-small'],
    ['smallMobile2', 'card-mobile-new-small'],
    // The mobile gallery is very slightly bigger than the card image and using
    // it on the listing could boost the LCP of the detail (which reuses the
    // image) for the categories with large cards.
    ['largeMobile', 'card-mobile-new-large'],
]);
const sizeToWidth = new Map([
    ['smallDesktop', 200],
    ['largeDesktop', 270],
    // We don't set the mobile widths, they're flexible
]);
const sizeToHeight = new Map([
    ['smallDesktop', 200],
    ['largeDesktop', 220],
    ['smallMobile', 82],
    ['smallMobile2', 82],
    ['largeMobile', 250],
]);
class ImageSource {
    #src;
    #srcset;
    #width;
    #height;
    constructor(baseSource, targetSize, reduceDataUse) {
        const imageRule = sizeToImageRule.get(targetSize) ?? 'card-desktop-new-large';
        this.#src = `${baseSource}?rule=${imageRule}-1x-auto`;
        if (reduceDataUse) {
            // When the connection is slow or requests data saving, use only the 1x
            this.#srcset = this.#src;
        }
        else {
            this.#srcset = `${baseSource}?rule=${imageRule}-1x-auto 1x, ${baseSource}?rule=${imageRule}-2x-auto 2x, ${baseSource}?rule=${imageRule}-3x-auto 3x`;
        }
        this.#width = sizeToWidth.get(targetSize);
        this.#height = sizeToHeight.get(targetSize);
    }
    get src() {
        return this.#src;
    }
    get srcset() {
        return this.#srcset;
    }
    get width() {
        return this.#width;
    }
    get height() {
        return this.#height;
    }
}
const getTargetSize = (size, device, overrideImageVariant) => {
    switch (size) {
        case 'small': {
            if (device === 'mobile') {
                return overrideImageVariant === 'large'
                    ? 'smallMobile2'
                    : 'smallMobile';
            }
            else {
                return 'smallDesktop';
            }
        }
        case 'big': {
            if (device === 'mobile') {
                return 'largeMobile';
            }
            else {
                return 'largeDesktop';
            }
        }
    }
};
const placeholderSize = 40;
const CardImage = ({ subject, src: baseSource, lazy, images = 0, size, categoryId, assetsBase = 'https://assets.subito.it', className, hasActivePromotions, isItemAvailable, mobileImageVariant = 'normal', }) => {
    const { saveData, effectiveConnectionType, deviceMemory } = useClientHints();
    const reduceDataUse = shouldReduceDataUse({
        saveData,
        effectiveConnectionType,
        deviceMemory,
    });
    const targetSizeDesktop = getTargetSize(size, 'desktop');
    const imageSourceDesktop = new ImageSource(baseSource, targetSizeDesktop, reduceDataUse);
    const targetSizeMobile = getTargetSize(size, 'mobile', mobileImageVariant);
    const imageSourceMsite = new ImageSource(baseSource, targetSizeMobile, reduceDataUse);
    return (React.createElement(React.Fragment, null,
        hasActivePromotions && isItemAvailable && (React.createElement(ActivePromotionRibbon, null, "PROMO")),
        React.createElement("div", { className: classnames(className, classes.container, {
                [classes.large]: size === 'big',
                [classes.small]: size === 'small',
            }) },
            React.createElement("figure", { className: classes['photo-container'] }, baseSource != null ? (React.createElement("picture", null,
                React.createElement("source", { srcSet: imageSourceDesktop.srcset, width: imageSourceDesktop.width, height: imageSourceDesktop.height, media: `(min-width: ${DeviceWidth.tablet.min})` }),
                React.createElement("source", { srcSet: imageSourceMsite.srcset, width: imageSourceMsite.width, height: imageSourceMsite.height, media: `(max-width: ${DeviceWidth.mobile.max})` }),
                React.createElement("img", { className: classnames(classes.photo, targetSizeMobile, targetSizeDesktop), src: imageSourceDesktop.src, alt: subject, loading: lazy ? 'lazy' : 'eager', decoding: "async" }))) : (React.createElement("img", { src: `${assetsBase}/static/icons/categories/${categoryId}.svg`, alt: subject, width: placeholderSize, height: placeholderSize, className: classes.placeholder, loading: "lazy", decoding: "async" }))),
            images > 1 && (React.createElement("div", { className: classes.count },
                React.createElement(CameraIcon, { count: images, assetsBase: assetsBase }),
                React.createElement("span", { "aria-hidden": "true" }, images))))));
};

export { CardImage as default };
//# sourceMappingURL=CardImage.js.map
